import { useEffect, useState } from 'preact/hooks'
import { X } from 'lucide-preact'
import { useToast } from '@/hooks/useToast'
import { useLookups } from '@/hooks/useLookups'
import type { OptionDto } from '@/types/lookups'
import type { OnlineEventSpeakerViewDto } from '@/api/webs'
import type { SpeakerEditInput } from '@/hooks/useOnlineEventSpeakers'
import { contactsApi, type ContactPositionViewDto } from '@/api/contacts'
import type { CompanyLookupViewDto } from '@/api/companies'
import { isValidEmail } from '@/utils/validation'
import { useAIForm } from '@/hooks/useAIForm'
import { TextBoxField } from '@/components'
import { Select, type SelectOption } from '@/components/common/Select'
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat'
import { SpeakerHeadshotField } from './SpeakerHeadshotField'

interface Props {
  open: boolean
  onClose: () => void
  speaker: OnlineEventSpeakerViewDto | null
  onSave: (updated: SpeakerEditInput) => Promise<void> | void
  workspaceId?: string
  webId?: string
  campaignId?: string
}

type SpeakerEditForm = {
  name: string
  speakerType: string
  companyId: string
  companyName: string
  email: string
  title: string
  bio: string
}

/**
 * Edit-speaker side panel. Seeds from the stored speaker (so prior overrides are
 * preserved), lets the operator freely edit name, company, and headshot, and
 * re-pulls the LinkedIn companies only as an optional quick-fill.
 */
export function SpeakerEditPanel({ open, onClose, speaker, onSave, workspaceId, webId, campaignId }: Props) {
  const { showError } = useToast()
  const { getSpeakerTypes } = useLookups()
  const speakerTypeOptions: OptionDto[] = getSpeakerTypes() || []

  // UI-only state
  const [autoUpdate, setAutoUpdate] = useState(false)
  const [companies, setCompanies] = useState<CompanyLookupViewDto[]>([])
  const [positions, setPositions] = useState<ContactPositionViewDto[]>([])
  const [loadingCompanies, setLoadingCompanies] = useState(false)
  const [attemptedSave, setAttemptedSave] = useState(false)
  const [saving, setSaving] = useState(false)
  const [customHeadshotUrl, setCustomHeadshotUrl] = useState<string | null>(null)
  // The LinkedIn-derived photo shown as the default / revert target. When no custom
  // override is set, the resolved ProfilePicUrl IS the LinkedIn photo; otherwise we
  // best-effort refresh it from the contact re-enrichment below.
  const [linkedInPhotoUrl, setLinkedInPhotoUrl] = useState('')

  // Form + AI integration
  const { form } = useAIForm<SpeakerEditForm>({
    mode: 'onChange',
    defaultValues: {
      name: speaker?.Name || '',
      speakerType: speaker?.SpeakerType || '',
      companyId: speaker?.CompanyId || '',
      companyName: speaker?.CompanyName || '',
      email: speaker?.Email || '',
      title: speaker?.Title || '',
      bio: speaker?.Bio || '',
    }
  }, {
    aiContext: {
      campaignId,
      webId,
      location: ChatLocations.WebSpeakerEditor,
      entityType: ChatEntityTypes.Web,
      entityId: webId,
      viewMode: ChatViewModes.Edit,
    },
    fields: open && speaker ? [
      { aiName: 'speakerBio', formPath: 'bio', displayName: 'Speaker Bio', fieldType: 'textarea', maxLength: 1000 },
      { aiName: 'speakerTitle', formPath: 'title', displayName: 'Speaker Title', fieldType: 'text', maxLength: 200 },
    ] : [],
    loadedData: open && speaker ? { speakerName: speaker.Name, speakerCompany: speaker.CompanyName } : undefined,
    contextSuppressed: !open,
  })
  const { register, control, watch, reset, setValue } = form

  // Watch form values for validation
  const nameValue = watch('name')
  const emailValue = watch('email')
  const titleValue = watch('title')
  const bioValue = watch('bio')
  const speakerTypeValue = watch('speakerType')
  const companyNameValue = watch('companyName')
  const companyIdValue = watch('companyId')

  // Reset when opened
  useEffect(() => {
    if (!open || !speaker) return
    reset({
      name: speaker.Name || '',
      speakerType: speaker.SpeakerType || '',
      companyId: speaker.CompanyId || '',
      companyName: speaker.CompanyName || '',
      email: speaker.Email || '',
      title: speaker.Title || '',
      bio: speaker.Bio || '',
    })
    setAutoUpdate(false)
    setCompanies([])
    setPositions([])
    setCustomHeadshotUrl(speaker.CustomHeadshotUrl ?? null)
    // With no override, the resolved photo is the LinkedIn photo; otherwise wait for re-enrichment.
    setLinkedInPhotoUrl(speaker.CustomHeadshotUrl ? '' : (speaker.ProfilePicUrl ?? ''))
    if (speaker.ContactId) {
      void enrichFromContactId(speaker.ContactId)
    }
  }, [open, speaker?.ContactId])

  // Close on Escape (matches the app's flyout/panel convention), but never mid-save.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, saving])

  const enrichFromContactId = async (contactId: string) => {
    setLoadingCompanies(true)
    try {
      const res = await contactsApi.lookupByContactId(contactId, { includeCompanies: true, includeSchools: false })
      if (res.data) {
        setCompanies(res.data.Companies || [])
        setPositions(res.data.Positions || [])
        // Keep a LinkedIn photo available as the revert target even when a custom override is active.
        if (res.data.ProfilePicUrl) setLinkedInPhotoUrl(prev => prev || res.data!.ProfilePicUrl || '')
        const currentCompanyId = form.getValues('companyId')
        if (contactId && currentCompanyId) {
          const comp = (res.data.Companies || []).find(c => c.Id === currentCompanyId)
          if (comp) setValue('companyName', comp.Name || comp.UniversalName || comp.NormalizedName || '')
        }
      } else if (res.error) {
        showError('Lookup failed', res.error)
      }
    } catch (e) {
      showError('Lookup error', (e as Error).message)
    } finally {
      setLoadingCompanies(false)
    }
  }

  // Selecting a LinkedIn company is an optional quick-fill; company name stays freely editable.
  const handleSelectCompany = (cid: string) => {
    setValue('companyId', cid)
    if (!cid) return
    const comp = companies.find(c => c.Id === cid)
    if (comp) {
      setValue('companyName', comp.Name || comp.UniversalName || comp.NormalizedName || '')
    }
    if (autoUpdate) {
      const related = positions.filter(p => p.CompanyId === cid)
      if (related.length) {
        const latest = related.sort((a, b) => {
          const ad = a.StartDate ? Date.parse(a.StartDate) : 0
          const bd = b.StartDate ? Date.parse(b.StartDate) : 0
          return bd - ad
        })[0]
        if (latest?.Title) setValue('title', latest.Title.slice(0, 200))
        if (latest?.Description) setValue('bio', latest.Description.slice(0, 1000))
      }
    }
  }

  // Validation
  const nameRequiredError = attemptedSave && !nameValue.trim()
  const nameLengthExceeded = nameValue.trim().length > 200

  const emailValid = isValidEmail(emailValue)
  const emailRequiredError = attemptedSave && !emailValue.trim()
  const emailInvalidError = !!emailValue.trim() && !emailValid
  const emailLengthExceeded = emailValue.trim().length > 254

  const titleRequiredError = attemptedSave && !titleValue.trim()
  const titleLengthExceeded = titleValue.trim().length > 200

  const speakerTypeRequiredError = attemptedSave && !speakerTypeValue.trim()
  const companyNameLengthExceeded = companyNameValue.trim().length > 200
  const bioLengthExceeded = bioValue.trim().length > 1000

  const canSave = speaker
    && nameValue.trim()
    && !nameLengthExceeded
    && !emailRequiredError
    && !emailInvalidError
    && !emailLengthExceeded
    && !titleRequiredError
    && !titleLengthExceeded
    && !speakerTypeRequiredError
    && !companyNameLengthExceeded
    && !bioLengthExceeded
    && emailValue.trim()
    && titleValue.trim()
    && companyNameValue.trim()
    && speakerTypeValue.trim()

  const handleSave = async () => {
    setAttemptedSave(true)
    if (!speaker || !canSave) return
    setSaving(true)
    try {
      const formValues = form.getValues()
      let finalCompanyName = formValues.companyName.trim()
      if (formValues.companyId && !finalCompanyName) {
        const comp = companies.find(c => c.Id === formValues.companyId)
        if (comp) finalCompanyName = (comp.Name || comp.UniversalName || comp.NormalizedName || '').trim()
      }
      await onSave({
        ContactId: speaker.ContactId,
        Name: formValues.name.trim(),
        SpeakerType: formValues.speakerType.trim(),
        CompanyId: formValues.companyId || undefined,
        CompanyName: finalCompanyName,
        Email: formValues.email.trim(),
        Title: formValues.title.trim(),
        Bio: formValues.bio.trim() || undefined,
        CustomHeadshotUrl: customHeadshotUrl ?? null,
      })
      onClose()
    } catch (e) {
      showError('Update failed', (e as Error).message || 'Unable to update participant')
    } finally {
      setSaving(false)
    }
  }

  const speakerTypeSelectOptions: SelectOption[] = speakerTypeOptions.map(o => ({ value: o.Key, label: o.Description }))

  if (!open || !speaker) return null

  return (
    <div class="fixed inset-0 z-50 flex" aria-modal="true" role="dialog">
      <div class="flex-1 bg-slate-900/40" onClick={() => !saving && onClose()} />
      <div class="w-full sm:w-[480px] lg:w-[540px] ml-auto h-full border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl flex flex-col">
        {/* Header */}
        <div class="flex items-start justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 class="text-lg font-semibold text-slate-900 dark:text-white">Edit speaker</h3>
          <button
            type="button"
            onClick={() => !saving && onClose()}
            class="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Close panel"
          >
            <X class="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div class="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <SpeakerHeadshotField
            name={nameValue}
            linkedInPhotoUrl={linkedInPhotoUrl}
            customUrl={customHeadshotUrl}
            onChange={setCustomHeadshotUrl}
            workspaceId={workspaceId}
            webId={webId}
          />

          <TextBoxField
            control={control}
            name="name"
            labelSize="xs"
            label="Name"
            maxLength={200}
            placeholder="Speaker name"
            error={nameRequiredError ? 'Speaker Name is required.' : nameLengthExceeded ? 'Speaker Name exceeds maximum length of 200 characters.' : undefined}
          />

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              labelSize="xs"
              label="Role"
              options={speakerTypeSelectOptions}
              placeholder="Select role"
              error={speakerTypeRequiredError ? 'Invalid SpeakerType.' : undefined}
              {...register('speakerType')}
            />
            {companies.length > 0 && (
              <div>
                <div class="flex items-center justify-between mb-1">
                  <label class="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Quick-fill from LinkedIn</label>
                  <label class="inline-flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-300 font-medium">
                    <input
                      type="checkbox"
                      checked={autoUpdate}
                      onChange={(e: Event) => setAutoUpdate((e.currentTarget as HTMLInputElement).checked)}
                      class="w-3.5 h-3.5 rounded border-slate-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500"
                    />
                    <span>+ Title &amp; Bio</span>
                  </label>
                </div>
                <Select
                  name="companyId"
                  options={companies.map(c => ({ value: c.Id, label: c.Name || c.UniversalName || c.NormalizedName || 'Company' }))}
                  placeholder="Optional"
                  value={companyIdValue}
                  onChange={(e: Event) => handleSelectCompany((e.currentTarget as HTMLSelectElement).value)}
                />
              </div>
            )}
            {loadingCompanies && companies.length === 0 && (
              <div class="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 self-end pb-2">
                <div class="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" /> Loading…
              </div>
            )}
          </div>

          <TextBoxField
            control={control}
            name="companyName"
            labelSize="xs"
            label="Company"
            maxLength={200}
            placeholder="Company name"
            error={attemptedSave && !companyNameValue.trim() ? 'Company Name is required.' : companyNameLengthExceeded ? 'Company Name exceeds maximum length of 200 characters.' : undefined}
          />

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextBoxField
              control={control}
              name="title"
              labelSize="xs"
              label="Title"
              maxLength={200}
              error={titleRequiredError ? 'Speaker Title is required.' : titleLengthExceeded ? 'Speaker Title exceeds maximum length of 200 characters.' : undefined}
              placeholder="Title"
            />
            <TextBoxField
              control={control}
              name="email"
              labelSize="xs"
              label="Email"
              type="email"
              maxLength={254}
              error={emailRequiredError ? 'Speaker Email is required.' : emailInvalidError ? 'Speaker Email is invalid.' : emailLengthExceeded ? 'Speaker Email exceeds maximum length of 254 characters.' : undefined}
              placeholder="name@example.com"
            />
          </div>

          <TextBoxField
            control={control}
            name="bio"
            labelSize="xs"
            label="Bio (optional)"
            multiline
            rows={5}
            showCharCounter
            maxLength={1000}
            error={bioLengthExceeded ? 'Speaker Bio exceeds maximum length of 1000 characters.' : undefined}
            placeholder="Short bio"
          />
        </div>

        {/* Footer */}
        <div class="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={() => !saving && onClose()}
            class="px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSave || saving}
            onClick={handleSave}
            class="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saving && <div class="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />}
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
